import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import moment from "moment";
import EditIcon from "@material-ui/icons/Edit";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import SearchIcon from "@material-ui/icons/Search";
import AssignmentCheckIcon from "@material-ui/icons/AssignmentTurnedIn";
import AssignmentIcon from "@material-ui/icons/Assignment";
import Icon from "@material-ui/core/Icon";
import { withRouter } from "react-router-dom";
import { useChatContext } from "stream-chat-react";
import { AuthContext } from "../../../../../auth-context";
import { DashboardContext } from "../../../dashboard-context";
import { getOrCreatePortalChannel, loadProfile, setCoachingActualEndDate } from "../../../../../api";
import config from "../../../../../config";
import { Row, Action } from "../../../../../components/action-button";
import { hostDateToString } from "../../inspect/util";
import { SurveyModal } from "../../surveys/components/survey-modal";
import CoachingEndDateModal from "./coaching-end-date-modal";
import { formatCoachingPlanName } from "../../../../../coaching-billing";

const TableTitle = styled.h2`
  padding-bottom: 8px;
  color: #181818;
  font-family: ${(props) => props.theme.font.primary};
  font-size: 1.25rem;
  font-weight: 600;
`;

const TableContainer = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0px 0px 8px 0px #e1e2e6;
  max-height: 460px;
  margin: 0 auto;
  overflow: auto;
  position: relative;
`;

const Table = styled.table`
  background: #fff;
  border-spacing: 0px;
  width: 100%;

  td,
  th {
    font-size: 0.9rem;
  }
`;

const TableHead = styled.thead`
  color: #fff;
`;

const TableHeadRow = styled.tr``;

const TableBody = styled.tbody``;

const HeadCell = styled.th`
  background: #070c29 !important;
  font-family: ${(props) => props.theme.font.primary};
  font-weight: 400;
  padding: 4px 8px;
  position: sticky;
  text-align: left;
  top: 0;
  white-space: nowrap;
  z-index: 30;
`;

const DataCell = styled.td`
  border-bottom: 1px solid #e8e8e8;
  color: #181818;
  font-family: ${(props) => props.theme.font.secondary};
  padding: 4px 8px;
  text-align: left;
  white-space: nowrap;
`;

const ActualEndDateCell = styled(DataCell)`
  position: relative;
  vertical-align: middle;
`;

const AttentionText = styled.span`
  align-items: center;
  color: #d91f1f;
  display: inline-flex;
  font-size: 0.7rem;
  font-weight: 600;
  gap: 4px;
  left: 8px;
  line-height: 1;
  position: absolute;
  top: 2px;

  svg {
    height: 13px;
    width: 13px;
  }
`;

const SubscriptionEndDateWarning = styled.span`
  align-items: center;
  color: #d91f1f;
  display: flex;
  font-size: 0.7rem;
  font-weight: 600;
  gap: 4px;
  line-height: 1.2;
  margin-bottom: 2px;

  svg {
    height: 13px;
    width: 13px;
  }
`;

const TableBodyRow = styled.tr`
  &.coaching-plan-nutrition {
    background-image: linear-gradient(#00eb45,#0ea039);
    background-repeat: no-repeat;
    background-size: 6px 100%;
  }

  &.coaching-plan-exercise {
    background-image: linear-gradient(#FFC000,#FFC000);
    background-repeat: no-repeat;
    background-size: 6px 100%;
  }

  &:hover {
    background-color: #f2f2f2;
  }

  &.coaching-plan-nutrition:hover {
    background-color: #f2f2f2;
    background-image: linear-gradient(#00eb45,#0ea039);
    background-repeat: no-repeat;
    background-size: 6px 100%;
  }

  &.coaching-plan-exercise:hover {
    background-color: #f2f2f2;
    background-image: linear-gradient(#FFC000,#FFC000);
    background-repeat: no-repeat;
    background-size: 6px 100%;
  }
`;

function getAccountCoachingPlan(account) {
  if (!account || !account.coaching_plan) {
    return null;
  }

  if (typeof account.coaching_plan === "string") {
    try {
      return JSON.parse(account.coaching_plan);
    } catch (err) {
      return null;
    }
  }

  return account.coaching_plan;
}

function hasCoachingPlanInfo(coachingPlan) {
  return !!coachingPlan &&
    typeof coachingPlan === "object" &&
    Object.keys(coachingPlan).length > 0 &&
    !!(
      coachingPlan.plan_name ||
      coachingPlan.purchase_date ||
      coachingPlan.actual_end_date ||
      coachingPlan.end_date ||
      coachingPlan.price ||
      coachingPlan.subscription_id || coachingPlan.transaction_id
    );
}

function isActiveCoachingPlan(coachingPlan) {
  const endDateValue = coachingPlan && (coachingPlan.actual_end_date || coachingPlan.end_date);
  if (!endDateValue) {
    return false;
  }

  const endDate = moment.utc(endDateValue);
  return endDate.isValid() && endDate.isSameOrAfter(moment.utc(), "day");
}

function getCoachingPlanBarClass(coachingPlan) {
  if (!isActiveCoachingPlan(coachingPlan)) {
    return "";
  }

  const planName = coachingPlan && coachingPlan.plan_name;
  if (/^(?:DISCOUNTED )?PILOT NUTRITION \+ EXERCISE (?:1|3|6|9|12) MONTHS?$/.test(planName)) {
    return "coaching-plan-exercise";
  }
  if (/^(?:DISCOUNTED )?PILOT NUTRITION (?:1|3|6|9|12) MONTHS?$/.test(planName)) {
    return "coaching-plan-nutrition";
  }
  return "";
}

function formatPlanName(planName) {
  return formatCoachingPlanName(planName, "Coaching plan");
}

function formatDate(date) {
  if (!date) {
    return null;
  }

  const parsedDate = moment.utc(date);
  return parsedDate.isValid() ? parsedDate.format("MMMM D, YYYY") : date;
}

function formatAssignedTrainers(account, team) {
  if (!account.trainers || account.trainers.length === 0 || !team || team.length === 0) {
    return "Unassigned";
  }

  const assignedTrainers = team
    .filter((trainer) => account.trainers.includes(trainer.id))
    .map((trainer) => trainer.name || trainer.full_name || trainer.email_address)
    .filter(Boolean);

  return assignedTrainers.length > 0 ? assignedTrainers.join(", ") : "Unassigned";
}

function needsActualEndDateAttention(actualEndDate) {
  if (!actualEndDate) {
    return true;
  }

  const endDate = moment.utc(actualEndDate);
  return endDate.isValid() && endDate.diff(moment.utc(), "days") < 21;
}

function actualEndDateExceedsSubscriptionEnd(coachingPlan) {
  if (!coachingPlan || coachingPlan.monthly !== true || !coachingPlan.actual_end_date || !coachingPlan.end_date) {
    return false;
  }

  const actualEndDate = moment.utc(coachingPlan.actual_end_date);
  const subscriptionEndDate = moment.utc(coachingPlan.end_date);
  return actualEndDate.isValid() && subscriptionEndDate.isValid() && actualEndDate.isAfter(subscriptionEndDate, "day");
}

function getActualEndDateStatusText(actualEndDate) {
  if (!actualEndDate) {
    return "Not set";
  }

  const endDate = moment.utc(actualEndDate);
  if (!endDate.isValid()) {
    return null;
  }

  const daysLeft = endDate.diff(moment.utc(), "days");
  if (daysLeft < 0) {
    return "Ended";
  }

  if (daysLeft === 0) {
    return "Ends today";
  }

  return `${daysLeft} ${daysLeft === 1 ? "day" : "days"} left`;
}

function getCoachingPlanRows(accounts) {
  return accounts
    .map((account) => ({
      account,
      coachingPlan: getAccountCoachingPlan(account),
    }))
    .filter((row) => hasCoachingPlanInfo(row.coachingPlan));
}

function CoachingPlanClientsTable({ accounts, history }) {
  const { user, updateUser } = useContext(AuthContext);
  const { setShowMessages, setMessageChannel, openSnackbar } = useContext(DashboardContext);
  const { client, setActiveChannel } = useChatContext();
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [createdStreamUsers, setCreatedStreamUsers] = useState([]);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [responseUserId, setResponseUserId] = useState(null);
  const rows = getCoachingPlanRows(accounts || []);

  useEffect(() => {
    if (!user.messaging || !client) {
      return;
    }

    const streamIds = rows
      .filter(({ account }) => account.claimed === 1 && (user.role_id === 3 ? account.trainers.includes(user.id) : true))
      .map(({ account }) => `${config.STREAMCHAT.userPrefix}${account.user_id}`);

    if (streamIds.length === 0) {
      setCreatedStreamUsers([]);
      return;
    }

    const fetchStreamUsers = async () => {
      const createdUsers = [];
      for (let i = 0; i < streamIds.length; i += 100) {
        const streamIdsChunk = streamIds.slice(i, i + 100);
        const result = await client.queryUsers(
          { id: { $in: streamIdsChunk } },
          { last_active: -1 },
          { presence: false, limit: 100 }
        );
        result.users.forEach((streamUser) => {
          createdUsers.push(streamUser.id);
        });
      }

      setCreatedStreamUsers(createdUsers);
    };

    fetchStreamUsers();
  }, [accounts, client, user.id, user.messaging, user.role_id]);

  const handleMessage = async (clientID) => {
    const getId = await getOrCreatePortalChannel(user.id, clientID);
    const channelId = getId?.data?.result?.channel_id;

    if(channelId) {
      const filter = { id: channelId };
      const sort = { last_message_at: -1 };
      const options = { watch: true, state: true };
      const getChannel = await client.queryChannels(filter, sort, options);
      setActiveChannel(getChannel[0]);
      setShowMessages(true);
      setMessageChannel(getChannel[0]);
    }
  };

  const openEditModal = (account, coachingPlan) => {
    const endDate = coachingPlan.actual_end_date || coachingPlan.end_date;
    setSelectedAccount(account);
    setSelectedDay(endDate ? moment.utc(endDate).toDate() : new Date());
  };

  const closeEditModal = () => {
    if (isLoading) {
      return;
    }

    setSelectedAccount(null);
    setSelectedDay(null);
  };

  const handleSubmit = () => {
    if (!selectedAccount || !selectedDay) {
      openSnackbar("Please select an end date.", true);
      return;
    }

    setIsLoading(true);
    setCoachingActualEndDate(user.id, {
      account_id: selectedAccount.id,
      actual_end_date: moment(selectedDay).format("YYYY-MM-DD"),
    })
      .then(() => loadProfile(user.id))
      .then((res) => {
        updateUser(res.data.profile);
        openSnackbar("Coaching plan end date updated.", false);
        setIsLoading(false);
        setSelectedAccount(null);
        setSelectedDay(null);
      })
      .catch((err) => {
        const message = err.response && err.response.data && err.response.data.error
          ? err.response.data.error
          : "Unable to update coaching plan end date.";
        openSnackbar(message, true);
        setIsLoading(false);
      });
  };

  return (
    <>
      <TableTitle>Coaching Plan Clients</TableTitle>
      <TableContainer>
        <Table>
          <TableHead>
            <TableHeadRow>
              <HeadCell>Name</HeadCell>
              <HeadCell>Email</HeadCell>
              <HeadCell>Assigned Trainer</HeadCell>
              <HeadCell>Plan</HeadCell>
              <HeadCell>Last Purchase Date</HeadCell>
              <HeadCell>Actual End Date</HeadCell>
              <HeadCell style={{ textAlign: "center", width: "220px" }}>Actions</HeadCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableBodyRow>
                <DataCell colSpan="7">No coaching plan clients found.</DataCell>
              </TableBodyRow>
            ) : (
              rows.map(({ account, coachingPlan }) => {
                const actualEndDateNeedsAttention = needsActualEndDateAttention(coachingPlan.actual_end_date);
                const actualEndDateStatusText = getActualEndDateStatusText(coachingPlan.actual_end_date);
                const actualEndDateDiffersFromSubscription = actualEndDateExceedsSubscriptionEnd(coachingPlan);
                const chatID = `${config.STREAMCHAT.userPrefix}${account.user_id}`;
                const noSurveyCheckin = !(account.check_in_type === 1 || account.check_in_type === 2 || account.check_in_type === 12);
                const hasRecentSurvey = hostDateToString(account?.survey?.recent_survey_report_date) === hostDateToString(account.report_date);
                const hideResponse = !account.report_date || !account?.survey?.assigned_survey_id;
                const recentSurveyResponseDate = account?.survey?.recent_survey_created_at
                  ? moment.utc(account.survey.recent_survey_created_at).format("MMM D")
                  : null;
                const surveyIcon = hasRecentSurvey
                  ? <AssignmentCheckIcon style={{ fill: "#4cb050" }} />
                  : noSurveyCheckin
                    ? <AssignmentIcon style={{ fill: "#444444" }} />
                    : <ErrorOutlineIcon style={{ fill: "#dc544a" }} />;
                return (
                  <TableBodyRow className={getCoachingPlanBarClass(coachingPlan)} key={account.id}>
                    <DataCell>{account.name}</DataCell>
                    <DataCell>{account.email_address}</DataCell>
                    <DataCell>{formatAssignedTrainers(account, user.team)}</DataCell>
                    <DataCell>{formatPlanName(coachingPlan.plan_name)}</DataCell>
                    <DataCell>{formatDate(coachingPlan.purchase_date) || "Unset"}</DataCell>
                    <ActualEndDateCell needsAttention={actualEndDateNeedsAttention}>
                      {actualEndDateDiffersFromSubscription ? (
                        <SubscriptionEndDateWarning>
                          <ErrorOutlineIcon />
                          Actual end date differs from sub end date
                        </SubscriptionEndDateWarning>
                      ) : actualEndDateNeedsAttention ? (
                        <AttentionText>
                          <ErrorOutlineIcon />
                          {actualEndDateStatusText}
                        </AttentionText>
                      ) : null}
                      {formatDate(coachingPlan.actual_end_date) || "-"}
                    </ActualEndDateCell>
                    <DataCell>
                      <Row style={{ justifyContent: "center" }}>
                        <Action
                          hide={hideResponse}
                          disabled={!account?.survey?.assigned_survey_id}
                          tipText={hasRecentSurvey ? "Client has completed their check-in form" : noSurveyCheckin ? "Client has entered a starting weight. No check-in form is available." : "Client has not completed their check-in form"}
                          icon={surveyIcon}
                          text={recentSurveyResponseDate ? `Response ${recentSurveyResponseDate}` : "Response"}
                          onClick={() => {
                            if (hasRecentSurvey || account?.survey?.recent_survey_report_date) {
                              setResponseUserId(account.user_id);
                              setShowSurveyModal(true);
                            } else {
                              openSnackbar("Client has not completed their check-in form yet", true);
                            }
                          }}
                        />
                        <Action
                          icon={<SearchIcon />}
                          text="Inspect"
                          onClick={() => history.push(`/dashboard/inspect/${account.id}/overview`)}
                        />
                        <Action
                          icon={<EditIcon />}
                          text="Edit"
                          onClick={() => openEditModal(account, coachingPlan)}
                        />
                        {!!user.messaging ?
                          (createdStreamUsers.includes(chatID) && !account.linked_portal_id) ?
                            <Action
                              hideOnClick
                              icon={<Icon>message</Icon>}
                              text="Chat"
                              onClick={() => handleMessage(account.user_id)}
                            />
                          :
                            <Action
                              disabled
                              tipText={account.linked_portal_id ? `This user is a coach account for ${account.linked_portal_name}.` : "This user must sign in before messaging is available."}
                              icon={<Icon>message</Icon>}
                              text="Chat"
                              onClick={() => null}
                            />
                          :
                          null
                        }
                      </Row>
                    </DataCell>
                  </TableBodyRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <SurveyModal
        open={showSurveyModal}
        onClose={() => setShowSurveyModal(false)}
        clientUserID={responseUserId}
      />
      <CoachingEndDateModal
        clientName={selectedAccount && selectedAccount.name}
        isLoading={isLoading}
        onClose={closeEditModal}
        onSelectDay={setSelectedDay}
        onSubmit={handleSubmit}
        open={!!selectedAccount}
        selectedDay={selectedDay}
      />
    </>
  );
}

export default withRouter(CoachingPlanClientsTable);
